import { useCallback, useMemo, useRef } from 'react';
import { Button, ButtonProps } from 'primereact/button';
import { MenuItem } from 'primereact/menuitem';
import { SplitButton } from 'primereact/splitbutton';
import { Toast } from 'primereact/toast';
import { useCreateDocumentMutation } from '../documentsApi';
import { CurrentDocumentParams, useDocument } from '../hooks/useDocument';
import useDocumentUi from '../hooks/useDocumentUi';

export interface GenerateDocumentButtonProps {
  isPreview: boolean;
}

type GenerateDocumentProps = ButtonProps & GenerateDocumentButtonProps;

export interface CreateDocumentArgs extends CurrentDocumentParams {
  isPreview: boolean;
  sendForSignature?: boolean;
}

const GenerateDocumentButton = (props: GenerateDocumentProps) => {
  const { label, isPreview, className, tooltip, icon, iconPos, outlined, ...rest } = props;
  const [createDocument, documentMeta] = useCreateDocumentMutation({ fixedCacheKey: 'document' });

  const { documentIsGenerating, documentIsRefreshing } = useDocumentUi();

  const { currentParams } = useDocument();

  const toast = useRef<Toast>(null);

  const generateDocument = useCallback(
    async ({ objectTypeId, objectId, templateId, isPreview, sendForSignature }: CreateDocumentArgs) => {
      try {
        const response = await createDocument({
          templateId,
          objectTypeId,
          objectId,
          isLive: !isPreview,
          sendForSignature: !isPreview && sendForSignature,
        }).unwrap();

        if ('error' in response || !response?.name) throw new Error('Error creating document.');

        if (isPreview) return;

        toast.current?.show({
          severity: 'success',
          summary: sendForSignature ? 'Document Sent for Signature' : 'Document Generated',
          detail: sendForSignature ? (
            <>
              <b>{response.name}</b> has been saved to the <b>Notes</b> section of this object and sent for
              signature.
            </>
          ) : (
            <>
              <b>{response.name}</b> has been saved to the <b>Notes</b> section of this object.
            </>
          ),
          life: 5000,
        });
      } catch (error) {
        toast.current?.show({
          severity: 'error',
          summary: 'Error',
          detail: 'An error was encountered while generating the document.',
          life: 3000,
        });
      }
    },
    [createDocument],
  );

  const createCurrentDocument = useCallback(
    (sendForSignature = false) => {
      if (!currentParams) return;
      const { objectTypeId, objectId, templateId } = currentParams;

      generateDocument({ objectTypeId, objectId, templateId, isPreview, sendForSignature });
    },
    [currentParams, generateDocument, isPreview],
  );

  const splitButtonItems = useMemo<MenuItem[]>(
    () => [
      {
        label: 'Send for Signature',
        icon: 'pi pi-send',
        command: () => createCurrentDocument(true),
      },
    ],
    [createCurrentDocument],
  );

  const splitButtonIcon = typeof icon === 'function' ? undefined : icon;

  return (
    <>
      {isPreview ? (
        <Button
          disabled={documentMeta?.isLoading || !currentParams || documentIsGenerating || documentIsRefreshing}
          label={label}
          className={className}
          icon={icon}
          iconPos={iconPos}
          outlined={outlined}
          onClick={() => createCurrentDocument()}
          loading={documentIsRefreshing}
          {...rest}
        />
      ) : (
        <SplitButton
          disabled={documentMeta?.isLoading || !currentParams || documentIsGenerating || documentIsRefreshing}
          label={label || 'Generate'}
          appendTo='self'
          icon={splitButtonIcon}
          loading={documentIsGenerating}
          className={className}
          buttonClassName='w-full justify-around'
          menuButtonClassName='px-3'
          buttonProps={{ iconPos }}
          model={splitButtonItems}
          onClick={() => createCurrentDocument()}
          outlined={outlined}
        />
      )}
      <Toast ref={toast} position='bottom-right' />
    </>
  );
};

export default GenerateDocumentButton;
